import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import './sidebar.scss';
import SharedUtils from '../../Common/utils';
import { ReactComponent as LogoText } from '../../../../assets/LogoText.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/logo-icon.svg';
import {
  useLocation,
  useHistory
} from 'react-router-dom/cjs/react-router-dom.min';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  CircularProgress,
  Typography
} from '@mui/material';
import SupportIcon from './icons/Support';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import store from '../../../../reducers/store';
import saas_agreement from '../../../../assets/saas_agreement.txt';
import { METADATA_KEY, sideBarData } from '../../../../utils/utils';
import authHeader from '../../../../services/auth-header';
import backendService from '../../../../services/backend.service';
import { useSelector } from 'react-redux';
import Header from '../header/header';

const drawerWidth = 310;

const Drawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: '80px',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  color: '#c3c7dc',
  ...(open && {
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: 300
      }),
      overflow: 'hidden',
      backgroundColor: '#131B23'
    }
  }),
  ...(!open && {
    '& .MuiDrawer-paper': {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: 300
      }),
      backgroundColor: '#131B23',
      overflow: 'hidden',
      width: `calc(${theme.spacing(7)} + 1px)`,
      [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(10)} + 1px)`
      }
    }
  })
}));

const CollapsibleSidebar = React.memo(({ open, onMouseEnter, mouseLeave, showSidebarUserHeader = true }) => {
  const storedBrandImg = useSelector((state)=>state.header.brandImg || null);
  const [brandImg, setBrandImg] = React.useState(null);
  const [dnsPanel, setDnsPanel] = React.useState(null);
  const [mobilePanel, setMobilePanel] = React.useState(null);
  const [cdrPanel, setCdrPanel] = React.useState(null);
  const [upTimeFlag, setUpTimeFlag] = React.useState(null);
  const [raSupport, setRaSupport] = React.useState(null);
  const [activePath, setActivePath] = React.useState(false);
  const [activeAccordionItem, setActiveAccordionItem] = React.useState('');
  const [expanded, setExpanded] = React.useState(false);
  const location = useLocation();
  const navigate = useHistory();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const timeoutRef = React.useRef(null);
  const abortControllerRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const acceptButtonHandlerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      abortControllerRef.current?.abort();
      if (acceptButtonHandlerRef.current) {
        acceptButtonHandlerRef.current.button.removeEventListener(
          'click',
          acceptButtonHandlerRef.current.handler
        );
        acceptButtonHandlerRef.current = null;
      }
      document.getElementById('saasagreement-container')?.remove();
    };
  }, []);

  React.useEffect(() => {
    if(storedBrandImg){
      setBrandImg(storedBrandImg);
    }
    if(!storedBrandImg){
      setBrandImg(null);
    }
  }, [storedBrandImg]);

  const handleMouseLeave = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      mouseLeave();
      timeoutRef.current = null;
    }, 1500);
  }, [mouseLeave]);

  const handleMouseEnter = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const isPathActive = (path) => location.pathname.includes(path);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getSaasAgreement = async () => {
    if (isInitialized) return;

    abortControllerRef.current?.abort();
    setIsInitialized(true);
    const newController = backendService.createAbortController();
    abortControllerRef.current = newController;

    function waitForButton() {
      return new Promise((resolve) => {
        const button = document.getElementById('acceptBtn');
        if (!button) {
          resolve();
          return;
        }
        const handler = async () => {
          const res = await SharedUtils.apiPost('set_admin_saas_agreement', {}, false, false, false, {
            signal: newController.signal
          });
          if (!isMountedRef.current || newController.signal.aborted) {
            return;
          }
          if(res){
            var element = document.getElementById('saasagreement');
            if (element) {
              element.style.display = 'none';
            }
            element = document.getElementById('overlay');
            if (element) {
              element.style.display = 'none';
            }
            resolve();
          }
        };
        acceptButtonHandlerRef.current = { button, handler };
        button.addEventListener('click', handler, { once: true });
      });
    }

    try {
      const response = await SharedUtils.apiPost(
        'admin_saas_agreement',
        {},
        false,
        false,
        false,
        {
          signal: newController.signal
        }
      );
      if(response && response.length > 0){
        if (isMountedRef.current && !newController.signal.aborted ) {
          if(typeof response[0] === "boolean"){
            let show_saas_agreement = !response[0];
            if (show_saas_agreement) {
              var saas_agreement_text;
              await fetch(saas_agreement, { signal: newController.signal })
                .then((r) => r.text())
                .then((text) => {
                  saas_agreement_text = text;
                });

              if (!isMountedRef.current || newController.signal.aborted) {
                return;
              }
    
              const appRoot = document.getElementById('root');
              document.getElementById('saasagreement-container')?.remove();
              let customElement = document.createElement('div');
              customElement.setAttribute('id', 'saasagreement-container');
              customElement.innerHTML =
                '<div id="saasagreement" style="position:fixed;top:0;left:0;width:100%;height:100%;background-color:#131b23;z-index:9999;opacity:.5"></div><div id=overlay style="position:fixed;top:0;left:0;width:50%;height:90%;background-color:#4d5053;z-index:9999;overflow-y:auto;white-space:pre-wrap;color:#fff;font-size:12px;border:3px solid #1f2535;border-color:#314c68;border-radius:8px;margin-top: 20px;margin-left: 30%;"><div style="margin:15px;">' +
                saas_agreement_text +
                '<button id="acceptBtn" style="background: #000;border: unset;border-radius: 6px;color: #b7bcd2;font-family: Inter, sans-serif;font-size: 14px;font-weight: 600;height: 40px;width: 184px;margin-bottom: 15px;left: 40%;position: relative;">Accept</button></div></div>';
              appRoot.parentNode.insertBefore(customElement, appRoot);
              await waitForButton();
            }
          }
        }
      }
    } catch (error) {
      if (!backendService.isRequestCanceled?.(error) && error.name !== 'AbortError') {
        console.error('Error fetching EULA:', error);
      }
    }
  };

  const getMeta = () => {
    try {
      const metaData = JSON.parse(localStorage.getItem(METADATA_KEY)) || {};
      localStorage.setItem('client_id', metaData['client_id']);
      localStorage.setItem('client_name', metaData['client_name']);
      store.dispatch({
        type: 'TRIGGER_CLIENT_NAME_UPDATE',
        payload: true
      });
      setDnsPanel(!metaData['dns']);
      setMobilePanel(metaData['mobile'] || false);
      setCdrPanel(metaData['cdr']);
      setRaSupport(metaData['ra_support']);
      setUpTimeFlag(metaData['ui_show_uptime']);
      window.config.ff_fullpanel = !metaData['dns'];
      if (metaData['demo_days']) {
        const appRoot = document.getElementById('root');
        const customElement = document.createElement('div');
        customElement.innerHTML =
          '<div style=background-color:#f00;color:#000!important;;padding:10px;text-align:center;width:100%;height:40px;font-size:14px;>Your Trial ends in ' +
          metaData['demo_days'] +
          ' days</div>';
        appRoot.parentNode.insertBefore(customElement, appRoot);
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Meta fetch error:', error);
      }
    } 
  };

  React.useEffect(() => {
    if (!isInitialized) {
      loadLogo();
      getSaasAgreement();
    }
    return () => {
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogo = async () => {
    const logoController = backendService.createAbortController();
    const logoSignal = logoController.signal;
    try {
    const response = await SharedUtils.apiPost('brandimg', {}, false, false, false, { signal: logoSignal });
      if(logoSignal.aborted) return;
      if(response && response[0]){
        store.dispatch({
          type: "SET_BRAND_IMG",
          payload: response[0]
        })
      } else {
        store.dispatch({
          type: "SET_BRAND_IMG",
          payload: ""
        })
      }
    } catch (error) {
      if (!backendService.isRequestCanceled?.(error)) {
        console.error('Error loading brand image:', error);
      }
    }
  };

  React.useEffect(() => {
    setActivePath(location.pathname);
    setActiveAccordionItem(() => {
      let activeAccordion = '';
      sideBarData(dnsPanel)?.forEach((item) => {
        if (item.subItems) {
          item.subItems?.forEach((subItem) => {
            if (isPathActive(subItem.path)) {
              activeAccordion = item.expandKey;
              if (open) {
                setExpanded(item.expandKey);
              }
            }
          });
        }
      });
      return activeAccordion;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, open]);

  React.useEffect(() => {
    if (!open) {
      setExpanded(false);
    }
  }, [open]);

  React.useEffect(() => {
    const handleBrandImageChange = () => {
      const reduxLocalStorage = JSON.parse(localStorage.getItem('reduxState'));
      setBrandImg(reduxLocalStorage?.brandImage);
    };
  
    window.addEventListener('brandImageChange', handleBrandImageChange);
    return () => window.removeEventListener('brandImageChange', handleBrandImageChange);
  }, []);

  React.useEffect(() => {
      const handleMetadataLoaded = () => {
          getMeta();
      };
      window.addEventListener('metadata_loaded', handleMetadataLoaded, { once: true });
      
      return () => {
          window.removeEventListener('metadata_loaded', handleMetadataLoaded);
      };
  }, []);

  return (
    <Box
      sx={{
        display: !authHeader() ? 'none' : 'flex',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1
      }}
      className='custom-sidebar-accordion'
    >
      <Drawer
        variant='permanent'
        data-testid='sidebar-drawer'
        open={open}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter} 
        onMouseOver={onMouseEnter}
        className='sidebar-drawer-container'
        sx={{
          '& .MuiDrawer-paper': {
            zIndex: (theme) => theme.zIndex.modal - 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            msOverflowStyle: 'none'
          }
        }}
      >
        <List
          sx={{
            pt: '10px',
            height: '100%',
            maxHeight: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pb: 0,
            minHeight: 0,
          }}
          onMouseEnter={() => {
            onMouseEnter();
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <ListItem
              disablePadding
              sx={{ marginBottom: '10px' }}
              data-itemtype={'listItem'}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5
                }}
                onMouseOver={onMouseEnter}
                disableRipple
                disableTouchRipple
                onClick={() => {
                  setExpanded(false);
                  navigate.push('/');
                }}
                data-testid={'logo-icon'}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 0 : 'auto',
                    justifyContent: 'center',
                    color: '#c3c7dc'
                  }}
                >
                  <LogoIcon
                    width={30}
                    height={30}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <>
                      <LogoText width={'128px'} />
                    </>
                  }
                  sx={{
                    opacity: open ? 1 : 0,
                    marginTop: '0px',
                    marginLeft: '-5px',
                    color: '#c3c7dc',
                    transition: (theme) =>
                      theme.transitions.create('opacity', {
                        duration: theme.transitions.duration.shortest
                      })
                  }}
                />
              </ListItemButton>
            </ListItem>
            {brandImg && (
              <ListItem
                disablePadding
                sx={{ marginBottom: '30px' }}
                data-itemtype={'listItem'}
              >
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5
                  }}
                  disableRipple
                  disableTouchRipple
                  onClick={() => {}}
                  data-testid={'brand-img-icon'}
                  onMouseOver={onMouseEnter}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 0 : 'auto',
                      justifyContent: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      transition: (theme) =>
                        theme.transitions.create('width', {
                          duration: theme.transitions.duration.shortest
                        })
                    }}
                  >
                    <img
                      src={brandImg}
                      id='clogo'
                      style={{
                        width: open ? '120px' : '50px',
                        height: open ? '70px' : '50px',
                        maxWidth: open ? '120px' : '50px',
                        maxHeight: open ? '70px' : '50px',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      alt='brand_image'
                    />
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            )}
          </Box>
          <Box
            className='sidebar-nav-scroll'
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
          >
          {sideBarData(dnsPanel)?.map((item, index) => {
            const isActive =
              (item.path
                ? activePath === item.path
                : item.subItems?.some(
                    (subItem) => activePath === subItem.path
                  )) ||
              (item?.testId === 'overview' && activePath === '/');

            if (item.ffSaasVisRequired && !window.config.ff_saas_vis)
              return null;

            if (item.dnsPanelRequired && !dnsPanel && !(item.mobileOverride && mobilePanel)) return null;

            if(item.cdrPanelRequired && !cdrPanel) return null;

            if(item.uptimeFlagRequired && !upTimeFlag) return null;

            if (item.isMenu) {
              return (
                <Accordion
                  expanded={expanded === item.expandKey}
                  onChange={handleAccordionChange(item.expandKey)}
                  data-testid={item.testId}
                  data-itemtype={'menuItem'}
                  key={index}
                >
                  <AccordionSummary
                    expandIcon={open ? <ExpandMoreIcon style={{color: '#c3c7dc'}} /> : null}
                    sx={{
                      backgroundColor: isActive ? '#1F2535' : 'transparent',
                      border: isActive ? '2px solid #2E3452' : 'none',
                      borderRadius: '6px',
                      boxShadow: 'none',
                      transition: (theme) =>
                        theme.transitions.create('opacity', {
                          duration: theme.transitions.duration.shortest
                        }),
                      padding: '13px 10px 13px 16px',
                      marginTop: '10px',
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      '.MuiAccordionSummary-root.Mui-expanded': {
                        margin: '0 6px'
                      }
                    }}
                    data-testid={item.testId + '-expand-btn'}
                    onMouseOver={onMouseEnter}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: '30px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mr: open ? 'auto' : 0,
                        ml: open ? 0 : ''
                      }}
                    >
                      {item.icon(true)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          fontFamily={'Montserrat'}
                          fontWeight={500}
                          fontSize={14}
                          // color={isActive ? '#FFF' : '#6B718E'}
                          color={'#FFF'}
                          mt={index === 0 ? '-5px' : '3px'}
                        >
                          {item.text}
                        </Typography>
                      }
                      sx={{
                        opacity: open ? 1 : 0,
                        ml: 0.5,
                        my: 0,
                        transition: (theme) =>
                          theme.transitions.create('opacity', {
                            duration: theme.transitions.duration.shortest
                          })
                      }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {item.subItems?.map((subItem, index) => {
                        if (
                          subItem.ffSaasVisRequired &&
                          !window.config.ff_saas_vis
                        )
                          return null;
                        if (subItem.dnsPanelRequired && !dnsPanel && !(subItem.mobileOverride && mobilePanel)) return null;
                        if (subItem.cdrPanelRequired != undefined && subItem.cdrPanelRequired && !cdrPanel) return null;
                        if (subItem.uptimeFlagRequired != undefined && subItem.uptimeFlagRequired && !upTimeFlag) return null;
                        return (
                          <ListItem
                            key={`${subItem.text}-${index}`}
                            disablePadding
                            sx={{ my: '10px', pl: '2px' }}
                          >
                            <ListItemButton
                              sx={{
                                minHeight: 48,
                                justifyContent: 'center',
                                backgroundColor:
                                  isActive && isPathActive(subItem.path)
                                    ? '#1F2535'
                                    : 'transparent',
                                border:
                                  isActive && isPathActive(subItem.path)
                                    ? '2px solid #2E3452'
                                    : 'none',
                                borderRadius: '6px',
                                margin: '0px 6px',
                                alignItems: 'center',
                                padding: '13px 10px 13px 16px',
                                ':hover': {
                                  backgroundColor: '#1F2535'
                                }
                              }}
                              onClick={() => navigate.push(subItem.path)}
                              disableRipple
                              disableTouchRipple
                              data-testid={subItem.testId}
                            >
                              <ListItemText
                                primary={
                                  <Typography
                                    fontFamily={'Montserrat'}
                                    fontWeight={500}
                                    fontSize={14}
                                    // color={
                                    //   isActive && isPathActive(subItem.path)
                                    //     ? '#FFF'
                                    //     : '#6B718E'
                                    // }
                                    color={'#FFF'}
                                    mt={'0px'}
                                    pl={'35px'}
                                  >
                                    {subItem.text}
                                  </Typography>
                                }
                                sx={{
                                  opacity: open ? 1 : 0,
                                  ml: open ? 0.5 : 'auto',
                                  my: 0,
                                  transition: (theme) =>
                                    theme.transitions.create('opacity', {
                                      duration:
                                        theme.transitions.duration.shortest
                                    })
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            } else {
              return (
                <ListItem
                  key={`${item.text}-${index}`}
                  disablePadding
                  sx={{ my: '10px', pl: '2px' }}
                  data-itemtype={'listItem'}
                >
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'flex-start' : 'center',
                      backgroundColor: isActive ? '#1F2535' : 'transparent',
                      border: isActive ? '2px solid #2E3452' : 'none',
                      borderRadius: '6px',
                      margin: '0px 6px',
                      padding: '13px 10px 13px 16px',
                      ':hover': {
                        backgroundColor: '#1F2535'
                      }
                    }}
                    onClick={() => {
                      setExpanded(false);
                      navigate.push(item.path);
                    }}
                    disableRipple
                    disableTouchRipple
                    data-testid={item.testId}
                    onMouseOver={onMouseEnter}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: '30px',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mr: open ? 'auto' : 0
                      }}
                    >
                      {item.icon(true)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          fontFamily={'Montserrat'}
                          fontWeight={500}
                          fontSize={14}
                          // color={isActive ? '#FFF' : '#6B718E'}
                          color={'#FFF'}
                          mt={index === 0 ? '-5px' : '0px'}
                        >
                          {item.text}
                        </Typography>
                      }
                      sx={{
                        opacity: open ? 1 : 0,
                        ml: open ? 0.5 : 'auto',
                        my: 0,
                        transition: (theme) =>
                          theme.transitions.create('opacity', {
                            duration: theme.transitions.duration.shortest
                          })
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            }
          })}
          {raSupport && (
            <ListItem
              disablePadding
              sx={{ my: '10px', pl: '2px' }}
              data-itemtype={'listItem'}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  margin: '0px 6px',
                  alignItems: 'center',
                  padding: '13px 10px 13px 16px',
                  ':hover': {
                    backgroundColor: '#1F2535'
                  }
                }}
                onMouseOver={onMouseEnter}
                onClick={() => {
                  setExpanded(false);
                  window.open(
                    'https://help.redaccess.io',
                    '_blank',
                    'noreferrer'
                  );
                }}
                disableRipple
                disableTouchRipple
                data-testid={'support'}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                    mr: 'auto',
                    height: '30px',
                    width: '30px'
                  }}
                >
                  <SupportIcon isActive={true} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      fontFamily={'Montserrat'}
                      fontWeight={500}
                      fontSize={14}
                      color={'#FFF'}
                      mt={'0px'}
                    >
                      Support
                    </Typography>
                  }
                  sx={{
                    opacity: open ? 1 : 0,
                    ml: open ? 0.5 : 'auto',
                    my: 0,
                    transition: (theme) =>
                      theme.transitions.create('opacity', {
                        duration: theme.transitions.duration.shortest
                      })
                  }}
                />
              </ListItemButton>
            </ListItem>
          )}
          </Box>
          {showSidebarUserHeader && (
            <Box sx={{ flexShrink: 0, mt: 'auto', paddingLeft: open ? '0px' : '10px' }}>
              <Header open={open} placement="sidebar" />
            </Box>
          )}
        </List>
      </Drawer>
    </Box>
  );
});
export default CollapsibleSidebar;
